import {
  PersonalizedProductDetailsResponse,
  ProductPriceObj,
} from 'api/product/getPersonalizedProductDetails/types';
import IconExpandMore from 'assets/icons/expand-more.svg';
import cn from 'classnames';
import Badge from 'components/badge';
import { OrderType } from 'constants/order';
import { StaticPDPProduct, Variant } from 'modules/product-details/types';
import Image from 'next/legacy/image';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import PriceElement from '../price-element';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  couponPrices?: ProductPriceObj;
  hasExpandMore?: boolean;
  imageSize: number;
  isList?: boolean;
  orderType: OrderType;
  pricePerKilo?: number;
  product: StaticPDPProduct;
  selectedVariantDiscount?: PersonalizedProductDetailsResponse['variants'][number];
  variant: Variant;
  isOpen?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * Component that displays a single variant of a product.
 * Can be used to display the variants as list or single item for a selector.
 */
const VariantDisplay: React.FC<Props> = ({
  couponPrices,
  hasExpandMore = false,
  imageSize,
  isList,
  orderType,
  pricePerKilo,
  product,
  selectedVariantDiscount,
  variant,
  isOpen,
  onClick,
  ...props
}) => {
  if (!selectedVariantDiscount) return null;

  return (
    <button
      className={cn(
        'bg-other-white w-full p-3 outline-hidden transition-colors duration-200 ease-in-out',
        isList
          ? 'lg:hover:bg-grey-100'
          : isOpen
            ? 'border-primary-main rounded-lg border'
            : 'border-grey-200 rounded-lg border',
        product.variants.length === 1 && 'cursor-default'
      )}
      onClick={onClick}
      {...props}
    >
      <div className="flex items-center">
        <Image
          src={variant.image.originalSrc}
          alt={variant.image.altText ?? 'Product image'}
          width={imageSize}
          height={imageSize}
          className={cn(!variant.availableForSale && 'opacity-60')}
        />
        <div className="mr-2 ml-3">
          {!variant.availableForSale && (
            <Badge type="General" className="w-fit">
              <FormattedMessage id="pdp:buy-panel:product-out-stock" />
            </Badge>
          )}
          <span className={cn(!variant.availableForSale && 'opacity-60')}>
            {variant.title}
          </span>
        </div>

        <PriceElement
          className={cn(
            'mt-0! flex h-11! grow flex-col justify-center',
            !variant.availableForSale && 'opacity-60'
          )}
          couponPrices={couponPrices}
          orderType={orderType}
          product={product}
          selectedVariant={variant}
          selectedVariantDiscount={selectedVariantDiscount}
          pricePerKilo={pricePerKilo}
          size="small"
        />
        {product.variants.length > 1 && hasExpandMore && (
          <IconExpandMore className="text-text-secondary ml-1 h-6 w-6" />
        )}
      </div>
    </button>
  );
};

export default VariantDisplay;
